import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";

/** Rows per page, or "all": every row, loaded in chunks while the user scrolls. */
export type PerPage = number | "all";

export interface PagedResponse<T> {
  items: T[];
  total: number;
}

/** The page size in the address bar (`per`), else the one last chosen on this device, else `fallback`. */
export function readPerPage(
  value: string | null,
  allowed: readonly number[],
  storageKey: string,
  fallback: number,
) {
  let chosen = value;
  if (chosen === null) {
    try {
      chosen = localStorage.getItem(storageKey);
    } catch {
      // storage unavailable
    }
  }
  if (chosen === "all") return "all" as const;
  const size = Number(chosen);
  return allowed.includes(size) ? size : fallback;
}

export function rememberPerPage(storageKey: string, per: PerPage): void {
  try {
    localStorage.setItem(storageKey, String(per));
  } catch {
    // not remembered
  }
}

/**
 * One page of `per` rows (page `page`), or with "all" every row in chunks of `chunk`, appended as more are
 * requested. `first` is the first response, for whatever else it carries (search terms, exact match...).
 */
export interface Paged<R extends PagedResponse<unknown>> {
  first: R | undefined;
  items: R["items"] | undefined;
  total: number;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  offset: number;
  size: number;
}

export function usePaged<R extends PagedResponse<unknown>>(
  key: readonly unknown[],
  fetch: (offset: number, limit: number) => Promise<R>,
  per: PerPage,
  page: number,
  chunk: number,
  enabled = true,
): Paged<R> {
  const all = per === "all";
  const size = all ? chunk : per;
  const paged = useQuery({
    queryKey: [...key, "page", size, page],
    queryFn: () => fetch(page * size, size),
    enabled: enabled && !all,
    placeholderData: keepPreviousData,
  });
  const everything = useInfiniteQuery({
    queryKey: [...key, "all", chunk],
    queryFn: ({ pageParam }) => fetch(pageParam, chunk),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((count, p) => count + p.items.length, 0);
      return last.items.length > 0 && loaded < last.total ? loaded : undefined;
    },
    enabled: enabled && all,
  });
  if (all) {
    return {
      first: everything.data?.pages[0],
      items: everything.data?.pages.flatMap((p) => p.items) as R["items"] | undefined,
      total: everything.data?.pages[0]?.total ?? 0,
      hasMore: everything.hasNextPage,
      loadingMore: everything.isFetchingNextPage,
      loadMore: () => void everything.fetchNextPage(),
      offset: 0,
      size,
    };
  }
  return {
    first: paged.data,
    items: paged.data?.items as R["items"] | undefined,
    total: paged.data?.total ?? 0,
    hasMore: false,
    loadingMore: false,
    loadMore: () => undefined,
    offset: page * size,
    size,
  };
}
